import { useCallback, useEffect, useState } from 'react';
import {
  addMatchToSequence,
  createFundingSequence,
  fetchFundingSequence,
  fetchFundingSequences,
  generateFundingSequence,
  reorderFundingSequenceItems,
  updateFundingSequence,
  updateFundingSequenceItem,
} from '../../services/funding/fundingSequenceService';

export function useFundingSequence({ clientId, agencyId, sequenceId, autoLoad = true } = {}) {
  const [sequences, setSequences] = useState([]);
  const [activeSequence, setActiveSequence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadSequences = useCallback(async () => {
    if (!clientId || !agencyId) {
      setSequences([]);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingSequences(clientId, agencyId);
      setSequences(result.sequences);
      return result.sequences;
    } catch (err) {
      setError(err?.message || 'Failed to load sequences');
      setSequences([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [clientId, agencyId]);

  const loadSequence = useCallback(async (targetSequenceId = sequenceId) => {
    if (!targetSequenceId || !agencyId) {
      setActiveSequence(null);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingSequence(targetSequenceId, agencyId);
      setActiveSequence(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load sequence');
      setActiveSequence(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [sequenceId, agencyId]);

  const generate = useCallback(async (options = {}) => {
    if (!clientId || !agencyId) return null;

    setGenerating(true);
    setError(null);
    try {
      const result = await generateFundingSequence(clientId, agencyId, options);
      setActiveSequence(result);
      await loadSequences();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to generate sequence');
      throw err;
    } finally {
      setGenerating(false);
    }
  }, [clientId, agencyId, loadSequences]);

  const createManual = useCallback(async (payload) => {
    if (!clientId || !agencyId) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createFundingSequence(clientId, agencyId, payload);
      setActiveSequence(result);
      await loadSequences();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create sequence');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [clientId, agencyId, loadSequences]);

  const updateSequence = useCallback(async (targetSequenceId, payload) => {
    if (!targetSequenceId || !agencyId) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await updateFundingSequence(targetSequenceId, agencyId, payload);
      setActiveSequence(result);
      await loadSequences();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to update sequence');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, loadSequences]);

  const updateItem = useCallback(async (targetSequenceId, itemId, payload) => {
    if (!targetSequenceId || !itemId || !agencyId) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await updateFundingSequenceItem(targetSequenceId, itemId, agencyId, payload);
      await loadSequence(targetSequenceId);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to update sequence item');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, loadSequence]);

  const reorderItems = useCallback(async (targetSequenceId, items) => {
    if (!targetSequenceId || !agencyId) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await reorderFundingSequenceItems(targetSequenceId, agencyId, items);
      setActiveSequence(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to reorder sequence');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId]);

  const addMatch = useCallback(async (matchId, targetSequenceId) => {
    if (!matchId || !agencyId) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await addMatchToSequence(matchId, agencyId, targetSequenceId);
      setActiveSequence(result.sequence);
      await loadSequences();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to add match to sequence');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, loadSequences]);

  useEffect(() => {
    if (autoLoad && clientId && agencyId) {
      loadSequences().then((loaded) => {
        if (sequenceId) {
          loadSequence(sequenceId);
        } else if (loaded.length > 0) {
          setActiveSequence(loaded[0]);
        }
      });
    }
  }, [autoLoad, clientId, agencyId, sequenceId, loadSequences, loadSequence]);

  return {
    sequences,
    activeSequence,
    loading,
    generating,
    saving,
    error,
    loadSequences,
    loadSequence,
    generate,
    createManual,
    updateSequence,
    updateItem,
    reorderItems,
    addMatch,
    setActiveSequence,
  };
}
